import { ArrowUpRight, Star, X } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { apiUrl } from '../lib/apiBase';

export type FeedbackCurrentUser = {
  id: string;
  email?: string | null;
  name?: string | null;
};

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

function gatherClientMeta() {
  return {
    url: typeof window !== 'undefined' ? window.location.href : '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    screenResolution:
      typeof window !== 'undefined'
        ? `${window.screen.width}x${window.screen.height}`
        : '',
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
  };
}

export function FeedbackWidget({
  currentUser,
}: {
  currentUser?: FeedbackCurrentUser | null;
}) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<SubmitState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fadeOut, setFadeOut] = useState(false);

  const isLoggedIn = Boolean(currentUser?.id && currentUser.email);

  useEffect(() => {
    if (status !== 'success') return;
    setFadeOut(true);
    const timer = window.setTimeout(() => {
      setOpen(false);
      setFadeOut(false);
      setStatus('idle');
      setRating(0);
      setComment('');
      setEmail('');
      setError(null);
    }, 1600);
    return () => window.clearTimeout(timer);
  }, [status]);

  function closePanel() {
    setOpen(false);
    setError(null);
    setStatus('idle');
    setHoverRating(0);
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (rating < 1 || rating > 5) {
      setError('Select a rating from 1 to 5 stars.');
      return;
    }
    if (!comment.trim()) {
      setError('Add a short comment before submitting.');
      return;
    }
    if (!isLoggedIn && email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter a valid email or leave it blank.');
      return;
    }

    setStatus('submitting');
    setError(null);

    try {
      const res = await fetch(apiUrl('/api/v1/feedback'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          rating,
          comment: comment.trim(),
          ...(isLoggedIn ? {} : email.trim() ? { email: email.trim() } : {}),
          user: currentUser?.id
            ? {
                id: currentUser.id,
                email: currentUser.email ?? null,
                name: currentUser.name ?? null,
              }
            : null,
          meta: gatherClientMeta(),
        }),
      });

      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };

      if (!res.ok || body.ok === false) {
        throw new Error(body.error ?? `Submit failed (${res.status})`);
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unable to submit feedback');
    }
  }

  return (
    <>
      {open ? (
        <div
          className={`fixed inset-0 z-50 flex h-full w-full flex-col border-slate-800 bg-slate-900 transition-all duration-300 md:inset-auto md:bottom-24 md:left-6 md:h-auto md:w-[420px] md:rounded-2xl md:border md:shadow-[0_24px_60px_rgba(0,0,0,0.55)] ${
            fadeOut ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-100'
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Send feedback"
        >
          {status === 'success' ? (
            <div className="flex flex-1 flex-col items-center justify-center px-8 py-16 text-center md:px-8 md:py-20">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300">
                Received
              </p>
              <p className="mt-3 max-w-xs font-sans text-base text-slate-200">
                Thanks - your feedback was sent.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="flex h-full min-h-0 flex-1 flex-col md:min-h-[32rem]"
            >
              <header className="flex items-start justify-between gap-4 border-b border-slate-800/80 px-6 pb-5 pt-7 md:px-7 md:pt-8">
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-cyan-300">
                    Product feedback
                  </p>
                  <h2 className="mt-2 font-sans text-xl font-medium tracking-tight text-slate-100 md:text-[1.35rem]">
                    How is TractTrace working for you?
                  </h2>
                  <p className="mt-1.5 font-sans text-sm leading-relaxed text-slate-500">
                    Rating and a short note help us ship the right fixes.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close feedback"
                  onClick={closePanel}
                  className="mt-0.5 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-slate-800 bg-slate-950/60 text-slate-400 transition-colors duration-200 hover:border-slate-700 hover:text-slate-200 md:hidden"
                >
                  <X className="h-4 w-4" strokeWidth={2} />
                </button>
              </header>

              <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-6 md:gap-7 md:px-7 md:py-7">
                <div>
                  <p className="mb-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
                    Rating
                  </p>
                  <div
                    className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-4 md:px-4 md:py-5"
                    onMouseLeave={() => setHoverRating(0)}
                  >
                    <div className="flex items-center justify-between gap-1 sm:justify-center sm:gap-2">
                      {[1, 2, 3, 4, 5].map((value) => {
                        const active = (hoverRating || rating) >= value;
                        return (
                          <button
                            key={value}
                            type="button"
                            aria-label={`${value} star${value === 1 ? '' : 's'}`}
                            aria-pressed={rating === value}
                            onMouseEnter={() => setHoverRating(value)}
                            onFocus={() => setHoverRating(value)}
                            onBlur={() => setHoverRating(0)}
                            onClick={() => setRating(value)}
                            className="group cursor-pointer rounded-lg p-2 transition-transform duration-200 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40"
                          >
                            <Star
                              className={`h-8 w-8 transition-all duration-200 md:h-9 md:w-9 ${
                                active
                                  ? 'fill-cyan-300 text-cyan-300 drop-shadow-[0_0_12px_rgba(34,211,238,0.35)] group-hover:fill-emerald-300 group-hover:text-emerald-300'
                                  : 'fill-transparent text-slate-600 group-hover:text-cyan-500/70'
                              }`}
                              strokeWidth={1.5}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col">
                  <label
                    htmlFor="feedback-comment"
                    className="mb-2.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500"
                  >
                    Comment
                  </label>
                  <textarea
                    id="feedback-comment"
                    rows={5}
                    required
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="What should we improve?"
                    className="min-h-[8.5rem] w-full flex-1 resize-none rounded-xl border border-slate-800 bg-slate-950/80 px-4 py-3 font-sans text-sm leading-relaxed text-slate-200 outline-none transition-all duration-200 placeholder:text-slate-600 focus:border-emerald-500/45 focus:ring-2 focus:ring-emerald-500/20 md:min-h-[9.5rem]"
                  />
                </div>

                {!isLoggedIn ? (
                  <div>
                    <label
                      htmlFor="feedback-email"
                      className="mb-2.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500"
                    >
                      Email <span className="normal-case tracking-normal text-slate-600">(optional)</span>
                    </label>
                    <input
                      id="feedback-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full rounded-xl border border-slate-800 bg-slate-950/80 px-4 py-3 font-mono text-sm text-slate-200 outline-none transition-all duration-200 placeholder:text-slate-600 focus:border-emerald-500/45 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                ) : null}

                {error ? (
                  <p className="font-sans text-xs text-rose-400" role="alert">
                    {error}
                  </p>
                ) : null}
              </div>

              <footer className="mt-auto flex items-center justify-end gap-3 border-t border-slate-800/80 px-6 py-5 md:gap-3.5 md:px-7 md:py-6">
                <button
                  type="button"
                  onClick={closePanel}
                  className="cursor-pointer rounded-xl border border-slate-800 bg-transparent px-5 py-2.5 font-sans text-sm text-slate-400 transition-all duration-200 hover:border-slate-700 hover:bg-slate-950/60 hover:text-slate-200 active:scale-[0.97]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="btn-primary gap-2 px-5 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {status === 'submitting' ? (
                    'Sending…'
                  ) : (
                    <>
                      Send feedback
                      <ArrowUpRight className="h-4 w-4" strokeWidth={2.25} />
                    </>
                  )}
                </button>
              </footer>
            </form>
          )}
        </div>
      ) : null}

      <button
        type="button"
        aria-label={open ? 'Close feedback' : 'Open feedback'}
        onClick={() => {
          setOpen((prev) => !prev);
          setError(null);
          if (status === 'error') setStatus('idle');
        }}
        className={`fixed bottom-6 left-6 z-[60] flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-cyan-500/40 bg-gradient-to-br from-cyan-300 via-cyan-500 to-emerald-500 text-[#0B0F17] shadow-[0_12px_30px_rgba(6,182,212,0.35)] transition-all duration-200 hover:scale-105 active:scale-95 ${
          open ? 'pointer-events-none opacity-0 md:pointer-events-auto md:opacity-100' : ''
        }`}
      >
        <Star className="h-5 w-5 fill-current" />
      </button>
    </>
  );
}
